import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { NAVIGATION_LINKS } from '~/consts/navigation';

interface SpotlightEntry {
  path: string;
  label: string;
  section: string;
}

interface SpotlightContextValue {
  register: (entry: SpotlightEntry) => void;
  getRoutes: () => SpotlightEntry[];
}

const SpotlightContext = createContext<SpotlightContextValue>({
  register: () => undefined,
  getRoutes: () => [],
});

export function useSpotlightRoutes() {
  return useContext(SpotlightContext).getRoutes;
}

function titleCase(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Strips optional route params and returns the static portion of a path.
 * Returns null if the path contains required params.
 */
function extractStaticPath(routePath: string): string | null {
  const segments = routePath.split('/');
  const staticSegments: string[] = [];

  for (const segment of segments) {
    if (segment.startsWith(':')) {
      const isOptional = segment.endsWith('?') || /\)\?$/.test(segment);
      if (isOptional) break;
      return null;
    }
    staticSegments.push(segment);
  }

  const path = staticSegments.join('/');
  return path || '/';
}

function deriveLabel(path: string): string {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return 'Home';
  return segments.map(titleCase).join(' / ');
}

function deriveSection(path: string): string {
  const firstSegment = path.split('/').filter(Boolean)[0];
  return firstSegment ? titleCase(firstSegment) : 'Home';
}

interface SpotlightProviderProps {
  additionalRoutes?: SpotlightEntry[];
  children: React.ReactNode;
}

export function SpotlightProvider({ additionalRoutes, children }: SpotlightProviderProps) {
  const registryRef = useRef<Map<string, SpotlightEntry> | null>(null);
  const cachedRoutesRef = useRef<SpotlightEntry[] | null>(null);

  // Lazy-initialize the registry with NAVIGATION_LINKS and additionalRoutes
  // so they take priority over auto-derived labels from RouteScanner
  if (registryRef.current === null) {
    const registry = new Map<string, SpotlightEntry>();

    for (const link of NAVIGATION_LINKS) {
      registry.set(link.href, {
        path: link.href,
        label: link.label,
        section: link.label,
      });
      for (const subLink of link.subLinks ?? []) {
        registry.set(subLink.href, {
          path: subLink.href,
          label: subLink.label,
          section: link.label,
        });
      }
    }

    for (const route of additionalRoutes ?? []) {
      registry.set(route.path, route);
    }

    registryRef.current = registry;
  }

  const register = useCallback((entry: SpotlightEntry) => {
    if (!registryRef.current!.has(entry.path)) {
      registryRef.current!.set(entry.path, entry);
      cachedRoutesRef.current = null;
    }
  }, []);

  const getRoutes = useCallback(() => {
    if (!cachedRoutesRef.current) {
      cachedRoutesRef.current = Array.from(registryRef.current!.values());
    }
    return cachedRoutesRef.current;
  }, []);

  const value = useMemo(() => ({ register, getRoutes }), [register, getRoutes]);

  return <SpotlightContext.Provider value={value}>{children}</SpotlightContext.Provider>;
}

/**
 * Wraps children and recursively scans for React Router <Route> elements,
 * automatically registering their static paths in the spotlight.
 */
export function RouteScanner({ children }: { children: React.ReactNode }) {
  const { register } = useContext(SpotlightContext);

  useEffect(() => {
    function scan(nodes: React.ReactNode) {
      React.Children.forEach(nodes, (child) => {
        if (!React.isValidElement(child)) return;

        const path = (child.props as Record<string, unknown>)?.path;
        if (typeof path === 'string') {
          const staticPath = extractStaticPath(path);
          if (staticPath) {
            register({
              path: staticPath,
              label: deriveLabel(staticPath),
              section: deriveSection(staticPath),
            });
          }
        }

        const childChildren = (child.props as Record<string, unknown>)?.children;
        if (childChildren) {
          scan(childChildren as React.ReactNode);
        }
      });
    }

    scan(children);
  }, []); // Route structure is static, scan once on mount

  return <>{children}</>;
}

/**
 * Expands a tab/section-based route into individual spotlight entries.
 * Accepts a base path and a tabs map where keys are tab slugs and values have a `label` or `title` or `name` field.
 */
export function expandTabRoutes(
  basePath: string,
  section: string,
  tabs: Record<string, { label?: string; title?: string; name?: string }>,
): SpotlightEntry[] {
  return Object.entries(tabs).map(([key, tab]) => ({
    path: `${basePath}/${key}`,
    label: `${section} / ${tab.label ?? tab.title ?? tab.name ?? key}`,
    section,
  }));
}

/**
 * Expands cruise menu items (which use href + name) into spotlight entries.
 */
export function expandMenuRoutes(
  section: string,
  menus: ReadonlyArray<{ name: string; href: string }>,
): SpotlightEntry[] {
  return menus.map((item) => ({
    path: item.href,
    label: `${section} / ${item.name}`,
    section,
  }));
}

export type { SpotlightEntry };
